<template>
  <div class="atb-flex atb-py-6 atb-px-4">
    <div class="atb-flex-grow grid__height">
      <div class="eds-table-wrap atb-w-full atb-h-full">
        <AgGridVue
          class="ag-theme-eds atb-w-full atb-h-full"
          id="whereUsedGrid"
          :column-defs="columnDefs"
          :rowModelType="'infinite'"
          :paginationPageSize="rowsPerPage"
          :pagination="true"
          :suppressPaginationPanel="true"
          :suppressCellSelection="true"
          @grid-ready="onGridReady"
        />
        <Pagination
          v-model="currentPage"
          :page-count="totalPages"
          :total-rows="totalRows"
          :page-size="rowsPerPage"
          @pageClick="changePageAndRefresh"
          @pageSizeChange="changePageSizeAndRefresh"
          class="atb-bg-white"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
  export default { name: 'WhereUsedTab' };
</script>
<script lang="ts" setup>
  import 'ag-grid-community/dist/styles/ag-grid.css';
  import '@experian/ag-theme-eds/dist/ag-theme-eds.css';
  import ClickableCell from '@/app/shared/components/grid-cells/ClickableCell.vue';
  import Pagination from '@atb-ui/eds-vue/src/components/Pagination.vue';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { useI18n } from 'vue-i18n';
  import { toRefs, reactive, Ref } from 'vue';
  import { AddTabEvent } from '@/app/shared/types/events/add-tab-event.interface';
  import { AgGridEvent, GridApi } from 'ag-grid-community';
  import { usePagination } from '@/app/shared/hooks/use-pagination';

  interface WhereUsedTabProps {
    clientId: number;
    codeMetaInformationsId: number;
    readOnly: boolean;
    getWhereUsedData: any;
    whereUsedData: Array<any>;
  }
  const props = defineProps<WhereUsedTabProps>();

  interface CreateEvent {
    (_event: 'createNewTab', _params: AddTabEvent): void;
  }

  const emit = defineEmits<CreateEvent>();
  const { t } = useI18n();
  const { clientId, getWhereUsedData, whereUsedData } = toRefs(props);
  const gridApi = reactive({}) as Ref<GridApi | null>;
  const {
    currentPage,
    rowsPerPage,
    totalRows,
    totalPages,
    changePage,
    changePageSize,
    calculatePagination,
  } = usePagination(
    whereUsedData.value.length,
    whereUsedData.value.length,
    gridApi
  );

  const changePageAndRefresh = (page: number) => {
    changePage(page);
    gridApi.value?.setDatasource(
      getWhereUsedData.value(
        currentPage.value,
        rowsPerPage.value,
        calculatePagination
      )
    );
  };

  const changePageSizeAndRefresh = (newPageSize: number | string) => {
    changePageSize(newPageSize);
    gridApi.value?.setDatasource(
      getWhereUsedData.value(
        currentPage.value,
        rowsPerPage.value,
        calculatePagination
      )
    );
  };

  const addToTabs = async (
    filterId: number,
    filterName: string,
    codeType: string
  ) => {
    emit('createNewTab', {
      clientId: clientId.value,
      id: filterId,
      name: filterName,
      isNew: false,
      dirtyTab: new Map<string, boolean>(),
      codeType,
    });
  };

  const columnDefs = [
    {
      field: 'shortName',
      headerName: t('shortName'),
      suppressSizeToFit: true,
      sortable: true,
      cellRendererFramework: ClickableCell,
      cellRendererParams: {
        data: { clientId: clientId.value },
        onClick: addToTabs,
      },
    },
    {
      field: 'codeType',
      headerName: t('type'),
      suppressSizeToFit: false,
    },
    {
      field: 'systems',
      headerName: t('systems'),
      suppressSizeToFit: false,
      valueGetter: (params: any) => {
        return (
          params.data?.systems.map((elem: any) => elem.systemName).join(', ') ||
          ''
        );
      },
    },
  ];

  const onGridReady = (params: AgGridEvent) => {
    gridApi.value = params.api;
    gridApi.value?.setDatasource(
      getWhereUsedData.value(
        currentPage.value,
        rowsPerPage.value,
        calculatePagination
      )
    );
    gridApi.value.sizeColumnsToFit();
  };

  defineExpose({
    gridApi,
  });
</script>

<style scoped>
  .grid__height {
    min-height: calc(100vh - (245px));
  }
</style>
